/**
 * Payments — every repayment received. The daily cash view: what came in,
 * from whom, against which loan, and whether the provider confirmed it.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExportButton } from "@/components/staff/ExportButton";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";

type Row = Record<string, any>;

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const PRESETS = [
  { label: "Today", from: today(), to: today() },
  { label: "Last 7 days", from: daysAgo(6), to: today() },
  { label: "Last 30 days", from: daysAgo(29), to: today() },
  { label: "All time", from: "", to: "" },
];

const providerTone: Record<string, string> = {
  succeeded: "bg-success/10 text-success",
  pending_customer: "bg-warning/10 text-warning",
  processing: "bg-warning/10 text-warning",
  failed: "bg-destructive/10 text-destructive",
  expired: "bg-destructive/10 text-destructive",
};

export function PaymentsPage() {
  const { toast } = useToast();
  const [data, setData] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    if (search.trim()) params.set("search", search.trim());
    try {
      setData(await staffApi.paymentsReceived(params.toString() ? `?${params}` : ""));
    } catch (error) {
      toast({
        title: "Could not load payments",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [from, to, search, toast]);

  useEffect(() => { load(); }, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows: Row[] = data?.payments || [];

  const exportParams = (() => {
    const p = new URLSearchParams();
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    if (search.trim()) p.set("search", search.trim());
    return p.toString() ? `?${p}` : "";
  })();

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Payments</h1>
          <p className="text-sm text-muted-foreground">
            Every repayment received, newest first.
          </p>
        </div>
        <div className="flex gap-2">
          {/* Same filters as the table below, so the file matches the screen. */}
          <ExportButton dataset="payments" params={exportParams} />
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="mr-1 h-4 w-4" />Refresh
          </Button>
        </div>
      </header>

      {/* Period total — always matches the rows below */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground">Collected in this view</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">
              {formatCurrency(data?.total_amount || 0)}
            </p>
            <p className="text-xs text-muted-foreground">
              {data?.total_count || 0} payment{(data?.total_count || 0) === 1 ? "" : "s"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Button key={p.label} size="sm"
              variant={from === p.from && to === p.to ? "default" : "outline"}
              onClick={() => { setFrom(p.from); setTo(p.to); }}>
              {p.label}
            </Button>
          ))}
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        </div>
        <div className="flex gap-2">
          <div>
            <Label className="text-xs">Search</Label>
            <Input placeholder="Name, phone, or reference" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()} className="w-56" />
          </div>
          <Button variant="outline" className="self-end" onClick={load}>
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin" />
          ) : rows.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No payments in this period.
            </p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Borrower</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Loan</TableHead>
                <TableHead>Provider</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {p.date ? new Date(p.date).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{p.borrower_name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{p.borrower_phone}</p>
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatCurrency(p.amount)}
                    </TableCell>
                    <TableCell className="text-sm capitalize">
                      {String(p.method || "—").split("_").join(" ")}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.reference || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {p.loan_id ? (
                        <Link to={`/staff/loans/${p.loan_id}`} className="text-primary hover:underline">
                          {p.loan_ref}
                        </Link>
                      ) : <p>{p.loan_ref}</p>}
                      {p.outstanding_after != null && (
                        <p className="text-muted-foreground">
                          {formatCurrency(p.outstanding_after)} left
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.provider_status ? (
                        <Badge className={providerTone[p.provider_status] || "bg-muted"}>
                          {String(p.provider_status).split("_").join(" ")}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">recorded</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {data && data.returned < data.total_count && (
            <p className="pt-3 text-center text-xs text-muted-foreground">
              Showing {data.returned} of {data.total_count} — narrow the dates to see the rest.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
